/**
 * scanner.en — the English door check-in scanner catalog, and the content
 * source of truth for this namespace: every value below is verbatim from, or
 * a direct extension of, the confirmed studio proof in
 * the design studio's scanner feature (`features/scanner/*.ts`), carrying its verdict
 * grammar, verbatim colour/motion/copy, and camera-error taxonomy forward
 * into production. `scanner.de.ts` is its Sie translation (matching the
 * admin catalog's own Sie register), composed into the shared `en` object by
 * `messages.en.ts`.
 *
 * Namespace:
 *   - `session.*`      — sign-in-adjacent chrome not already covered by the
 *                        shared `auth.*` catalog: role/provisioning denial,
 *                        the event-picker step
 *                        (`scanner/src/sign-in.view.ts`,
 *                        `scanner/src/event-pick.view.ts`).
 *   - `scan.*`         — the camera surface: the existing single-message
 *                        camera-denied fallback, plus the `NotAllowedError`/
 *                        `NotFoundError`/`NotReadableError`/
 *                        `OverconstrainedError` taxonomy (`scan.cameraError.*`,
 *                        four distinct operator instructions, never one
 *                        generic "allow camera access"), the mode/network/
 *                        HID status readout, the scanned/remaining counter, and the
 *                        no-decode frame-diagnostic hint
 *                        (`scanner/src/scan.view.ts`,
 *                        `decode.worker.ts`).
 *   - `verdict.*`      — the door's decision. `verdict.headline.*` carries
 *                        the four terminal splash headlines — Admit / Already
 *                        In / Stop / Hold — deliberately built with no shared
 *                        headline word between them (a prior system rendered
 *                        an already-redeemed ticket as "Valid Ticket" in a
 *                        different colour, which fails the instant colour
 *                        alone has to carry the difference); `verdict.stop.*`
 *                        and `verdict.held.*` carry the one subline each
 *                        reason/cause needs; `verdict.verifyAtDoor.*` is
 *                        the write's refusal to admit a person-checked type
 *                        on a bare decode, as reported by a surface with no
 *                        confirm step of its own; `verdict.holder` names the
 *                        person the buyer handed the scanned ticket to, the
 *                        one line here that carries a name; `verdict.undo.*`
 *                        is the supervisor correction of a mistaken scan,
 *                        whichever door recorded it.
 *   - `settings.*`     — the operator settings page: check-in mode, the
 *                        accepted-ticket-type filter, the ticket-provenance
 *                        filter and the count the two of them admit between
 *                        them, sound/haptics toggles.
 *   - `manualLookup.*` — the supervisor dead-phone/escalation path
 *                        (`tickets.reports.view`-gated search vs. an ordinary
 *                        door's escalate-to-a-supervisor instruction).
 *   - `ticketSheet.*`  — the pending-admit confirm brake and the read-only
 *                        ticket detail pull-up.
 */
export const scanner = {
	session: {
		// The door phone's own mark, slotted into `sign-email`'s `brand` slot —
		// this device signs a doorperson in to check tickets, not a buyer in to
		// the promoter's storefront, so the workspace wordmark is not what
		// belongs above this field.
		title: "Check-in",
		subtitle: "Sign in to scan tickets at the door",
		notProvisioned:
			"This account is not set up for check-in. Contact your event manager.",
		noPermission:
			"This account cannot scan tickets. Contact your event manager.",
		signOut: "Sign out",
		retrySignOut: "Try signing out again",
		pickEvent: "Choose an event",
		noUpcomingEvents: "No upcoming events",
		noUpcomingEventsBody:
			"Ask an event manager to add one before you can check anyone in.",
		scanEvent: "Scan tickets for {{name}}, {{date}}",
		scanEventWithStatus: "Scan tickets for {{name}}, {{date}}, {{status}}",
		changeEvent: "Change event",
		noOrganization: "Open the scanner from the link your organizer sent you.",
		connecting: "Connecting…",
		offline: {
			title: "No connection",
			body: "Check the WiFi connection — we'll keep trying automatically.",
		},
	},
	scan: {
		cameraDenied:
			"We need the camera to scan tickets. Allow access, then try again.",
		retry: "Retry",
		torchOn: "Turn on flashlight",
		torchOff: "Turn off flashlight",
		torchFailed: "Flashlight unavailable. Try again.",
		idle: "Point the camera at a ticket",
		checking: "Checking…",
		cameraError: {
			acquiring: "Starting camera…",
			notAllowed: {
				title: "The camera is blocked",
				body: "Allow the camera in your phone's settings, then try again.",
			},
			notFound: {
				title: "No camera found",
				body: "This phone has no camera we can use.",
			},
			notReadable: {
				title: "Another app is using the camera",
				body: "Close the other app using the camera, then try again.",
			},
			overconstrained: {
				title: "This camera can't do that",
				body: "Try again — we'll set the camera to something simpler.",
			},
			decoderUnavailable: {
				title: "The scanner couldn't start",
				body: "Reload the page to keep scanning.",
			},
			tryAgain: "Try again",
			reload: "Reload",
		},
		status: {
			// Read inside the door line, where no caption is left to name the
			// dimension for it — so each value names its own.
			modeAuto: "Auto check-in",
			modeManual: "Manual check-in",
			networkSlow: "Slow connection",
			networkOffline: "Offline",
			externalScanner: "Handheld scanner",
		},
		// The scan screen's own identity readout: which event this phone is
		// scanning, and what it admits. Borrows the settings page's words
		// (`settings.acceptedTypes`, `settings.origin.*`) wherever they exist,
		// so the readout and the screen that changes it never name one filter
		// two ways. `types` is the collapsed form only — a single accepted type
		// reads as its own name, which is shorter than counting it.
		door: {
			noEvent: "No event chosen",
			allTypes: "All ticket types",
			types: "{{count}} ticket types",
			// Fills `lane` where this door derives its own set. A word whose
			// referent moves continuously, in front of a set that moves with
			// it — so at 02:00 "Now: 2 ticket types" and a bare "2 ticket
			// types" say different things about what this door admits at
			// 02:01.
			now: "Now",
			// What this door admits, prefixed only where it derived that set
			// itself. An operator's own pick carries no prefix: it is the set
			// every door has always printed bare. Absent entirely where the
			// door admits everything — an open door has no lane, so a prefix
			// would claim a derivation that did not happen and make an open
			// door sound narrowed.
			lane: "{{when}}: {{types}}",
		},
		findGuest: "Find guest",
		settings: "Settings",
	},
	verdict: {
		pendingAdmit: {
			hold: "Hold to admit",
			holdAriaLabel: "Press and hold to admit this guest",
			// The verification hold — the same card doing a different job, so
			// its actuator carries the claim the operator is making rather than
			// a bare verb: the finger lands on "I looked", not on "confirm".
			// `checkFallback` is the instruction wherever the type carries no
			// `doorNote` of its own, and it names the type because a bare
			// "Crew" tells a doorperson nothing to look at.
			verify: {
				hold: "Hold — I checked",
				holdAriaLabel: "Hold to admit — you have checked this guest",
				checkFallback:
					"Check that this guest qualifies for {{typeName}} before letting them in",
			},
		},
		valid: "Valid — Admit",
		alreadyRedeemed: "Already used",
		refunded: "Refunded — do not admit",
		invalid: "Invalid ticket",
		offline: "Offline — cannot verify",
		headline: {
			admit: "Admit",
			alreadyIn: "Already In",
			stop: "Stop",
			held: "Hold",
		},
		confirming: {
			default: "Confirming…",
			slow: "Still checking…",
		},
		admit: {
			partyRemaining: "{{count}} more from this order",
			partyAlreadyInSuffix: " · {{count}} already in",
		},
		// Who holds this ticket now — the buyer handed it on and typed this
		// name themselves, so it is read back verbatim. A statement of fact
		// beside the verdict, never an instruction: admission is decided on
		// the ticket alone and this line changes none of it.
		holder: "Handed to {{name}}",
		alreadyIn: {
			// The splash's own combined "when · which door" line (never a
			// standalone sentence — that duplicated the "Already In" headline
			// outright): self names its own door the same lowercase-generic
			// way `unknownDoor` names an unlabelled one, so the line reads
			// symmetrically for both cases — "42 minutes ago · your own door"
			// / "42 minutes ago · Tür 2".
			self: "your own door",
			stranger: "Already admitted",
			// Fills the door-name slot when the door-label map carries no label
			// for the uid that admitted — a door provisioned after those labels
			// were written. The uid itself never reaches the operator: a hex string
			// tells a bouncer nothing, while "another door" is both true and
			// actionable, since the self case is already told apart by uid.
			unknownDoor: "another door",
			// The re-attempt affordance both self and stranger already-in
			// verdicts carry (the adversarial-pass departure): re-checks the
			// server once, right now, for a resolved-not-stale answer — never
			// "Retry", which reads wrong on a verdict that already resolved.
			verify: "Verify",
		},
		stop: {
			wrongType: "{{typeName}} isn't valid at this entrance",
			// Names the entrance's own rule, never the ticket's defect: the guest
			// is at the wrong door, not holding a bad ticket. No interpolated
			// source name, because this door can be set to admit either one and
			// the line has to stay true in both directions — a sibling
			// "Guest tickets only" reads as a lie the moment the door is
			// sale-only, and the guest lane is exactly where that misread sends
			// someone to the back of the wrong queue.
			wrongOrigin: "This ticket goes through a different entrance",
			refunded: "This ticket was refunded",
			// The rule refused the write on the ticket's own frozen entryWindow —
			// not a race with another redemption — so the operator reads the
			// actual boundary rather than a generic refusal.
			tooLate: "Entry closed {{at}}",
			// The re-read couldn't explain the refusal (a parse failure, or an
			// in-window read the rejecting rule shouldn't disagree with) — no
			// boundary to show, so this is the one stop reason with no {{at}}.
			refused: "Couldn't check in — try again",
			unknown: "Unrecognized code",
		},
		held: {
			offline: "No connection — can't verify",
			timeout: "Still trying — connection is slow",
			body: "Ask them to stand aside a moment — keep scanning the rest of the line.",
			retry: "Retry",
			setAside: "Set aside — keep scanning",
		},
		// The refusal a check-in surface with no confirm step of its own can
		// only report: this type is admitted by a person, and all that reached
		// the write was a decode or a by-name tap. The note is the type's own
		// `doorNote` — the one thing staff is asked to look at — and
		// `noteFallback` stands in wherever the operator never wrote one, so
		// the toast still names an action rather than only a refusal.
		verifyAtDoor: {
			holdToast: "{{typeName}} is confirmed by a person — check: {{note}}",
			noteFallback: "whether this guest qualifies before letting them in",
		},
		undo: {
			action: "Undo admission",
			confirmTitle: "Undo this admission?",
			confirmBody:
				"The ticket returns to valid. Scan it again to admit this guest.",
			confirm: "Undo",
			cancel: "Keep it",
			success: "Admission undone",
		},
	},
	settings: {
		checkinMode: {
			label: "Check-in mode",
			auto: "Auto",
			manual: "Manual — confirm each ticket",
		},
		acceptedTypes: "What this entrance lets in",
		// The band's own axis: on, the accepted types are the ones whose entry
		// window contains this instant; off, the operator's own pick stands
		// until they change it. Named for the clock and not for the window it
		// reads, because a doorperson can watch a clock move. On a multi-day run
		// the set turns on the date too — the day columns under this row are
		// what say so, and naming both clips on a narrow phone. Never
		// "Automatic"/"Manual": the mode chips at the top of this same panel own
		// both words for an unrelated axis, and one surface cannot carry two
		// Automatics.
		goesByTheTimeOfDay: "Goes by the time of day",
		// The switch's own name, said in full because the visible label cannot
		// be: two switches stack here, and the visible label alone never says
		// WHAT goes by it.
		goesByTheTimeOfDayAria: "Ticket types go by the time of day",
		// The tray's caption — what the set inside it was drawn from, in whole
		// sentences rather than a stem and a clause joined in a template, so a
		// translator sets each line's own word order. A derived set is drawn
		// from this instant and says so in both modes, because watching this
		// line change at the flip is what teaches the door's own caption.
		validNow: "Valid now",
		// An operator's own pick names the moment they took the door. "at",
		// never "since": a clock time is not a threshold anything crosses, so
		// "at 21:14" is the same true sentence at 21:20 as at 02:00, where
		// "since" promises a duration and a clause that renders on every
		// hand-set door is one the eye learns to skip. A pick this door
		// recorded no instant for names none — an untimed pick is not a stale
		// one.
		ownSelection: "You picked these",
		ownSelectionAt: "You picked these at {{time}}",
		// The band groups by MEMBERSHIP, so one multi-day pass renders once per
		// day it admits on. Every instance would otherwise announce the same
		// name, leaving a reader — and any harness driving by accessible name —
		// unable to say which column's box they just ticked.
		typeOnDay: "{{name}} — {{day}}",
		// The day head's tri-state box, named for the SET it covers, never for
		// what ticking it does: a ticked box admits at a door, applies on a
		// voucher's scope and reveals on its unlocks, so any one of those verbs
		// is wrong on the other two mounts. Not named for the day either — the
		// column already announces that, so "Saturday 7 September" alone would
		// state where the box is instead of what it covers.
		allTypesOnDay: "All ticket types on {{day}}",
		allTypesWholeRun: "All ticket types that run the whole event",
		// The provenance axis, orthogonal to the type filter above it: a venue
		// running a separate guest entrance sets this door to one source. `all`
		// is the unfiltered default and the only option that admits both, which
		// is why the row is a three-way single choice and not two toggles.
		origin: {
			label: "Which tickets",
			all: "All",
			guests: "Guest tickets",
			sales: "Shop tickets",
		},
		// The two filters composed. Names the entrance rather than "this filter"
		// because what the operator is really being told is what their door will
		// do tonight — `0 of 214` is the reading this line exists to surface.
		admits: "This entrance lets in {{admitted}} of {{total}} tickets",
		// What the Auto chip alone overstates: a type flagged verify-at-door is
		// admitted by a person in Auto too. One type reads as its own name; past
		// one the title is the row's own label, carrying the count and opening
		// the names, because a per-day catalog puts twenty-odd of them on this
		// line. Neither string names the mode: the line renders only under Auto,
		// directly beneath the lit Auto chip, and on a door phone that clause
		// costs the one line this has to fit on.
		alwaysConfirmedSingular: "You always confirm {{names}} yourself",
		alwaysConfirmedTitle: "You always confirm these yourself",
		sound: "Sound",
		haptics: "Vibration",
		back: "Back",
		close: "Close",
	},
	// The door console mounted inside the scan surface: the resting readout an
	// operator glances at, and the journal of what this phone's own scans
	// actually did. Every stop's own sentence is `verdict.stop.*` verbatim —
	// the journal repeats what the splash said rather than paraphrasing it.
	doorLog: {
		// Read as "412 in · 188 still outside" — each number carries its own
		// word, because a caption row above two figures is a line of chrome on
		// the one readout that has to survive a glance over a moving queue.
		in: "in",
		stillOutside: "still outside",
		// Sits where the numbers are, not in a corner of some other pill: these
		// figures get quoted to a supervisor, so a door reading a cache-only
		// index has to say so at the point of quotation.
		notConnected: "Not connected",
		stale: "Not connected — numbers may be stale",
		nothingScanned: "Nothing scanned on this phone yet",
		// One trailing entry for every typeId this event's catalog cannot name.
		// The lane strip is one line wide, so this is a word rather than the
		// "Other ticket types" a full-width row could carry.
		otherLane: "Other",
		// The hour bands over the journal. The clock-hour band itself is
		// composed from two formatted times rather than translated, so only the
		// two named bands carry keys.
		band: { justNow: "Just now", thisHour: "Earlier this hour" },
		// Renders only once there is something to separate — on a clean shift
		// every row is an admission and the switch offers no choice at all.
		scope: {
			all: "Everything",
			refused: "Needs explaining · {{count}}",
		},
		// Never a bare "nothing here": a door reads two very different
		// situations off an empty journal, and only one is worth being pleased
		// about.
		empty: {
			all: "Scans will appear here as you take them",
			refused: "Nothing has needed explaining this shift",
		},
		// The tone mark's own accessible name, one per outcome. Deliberately
		// not `verdict.headline.*`: that set is four one-word shouts sized for
		// a full-screen splash, and this one names a 8px dot in a list.
		outcome: {
			admitted: "Admitted",
			alreadyIn: "Already in",
			notRecorded: "Not recorded",
			refused: "Refused",
		},
		row: {
			// Stands in for the type name on a row whose ticket this door's own
			// catalog cannot name — never the typeId, which tells a doorperson
			// nothing.
			ticket: "Ticket",
			// A scan the door never got an answer for: not a refusal, and not an
			// admission either.
			noAnswer: "No answer",
			// The purchaser, and only where they are someone else — a row
			// reading "Anna Weber · bought by: Anna Weber" states one fact
			// twice. Lower-case because it continues the name before it. The
			// name it follows carries no word of its own: a row showing one
			// person is showing whose ticket it is.
			boughtBy: "bought by: {{name}}",
		},
		detail: {
			// The one fact the splash showed and nothing kept: a party
			// half-admitted is the group that comes back in ten minutes asking
			// why their friends got in and they did not.
			partyRemaining: "{{count}} more still to come on this order",
			// The row's own leading time is when THIS scan happened; this one is
			// when the ticket actually went in. Two times on one row need the
			// second one named, and the door beside it is the half a doorperson
			// repeats out loud.
			admittedAtOtherDoor: "Admitted {{at}} · {{door}}",
			// Same door, seconds apart: a thumb that fired twice, not a ticket
			// doing the rounds — printing two clock times a minute apart says
			// the opposite of what happened.
			justScanned: "You scanned this a moment ago",
			youAdmittedAt: "You admitted this at {{at}}",
			heldOffline: "Offline — this scan was never recorded",
			heldTimeout: "Timed out — this scan was never recorded",
		},
	},
	manualLookup: {
		searchPlaceholder: "Search by name or email",
		escalateBody:
			"Find a supervisor — their phone can look this guest up by name. This phone can see {{count}} tickets but no names — that's on purpose, so guest details stay off the door phones.",
		noMatch: "No match.",
		noTypeMatch: "No guests hold this ticket type.",
		clearFilterHint: "Clear the filter to see everyone.",
		checkIn: "Check in",
		alreadyCheckedInBadge: "Already checked in",
		checkedInToast: "Checked in — {{typeName}}",
		checkInFailed: "Couldn't check in — try again",
		backToScanning: "Back to scanning",
		jumpToLetter: "Jump to {{letter}}",
		ticketCountSingular: "ticket",
		ticketCountPlural: "tickets",
		guestSheetAriaLabel: "{{name}}'s tickets",
	},
	ticketSheet: {
		pendingBadge: "Would admit — confirm below",
		alreadyInSelfBadge: "You already admitted this",
		alreadyInOtherBadge: "Already admitted",
		admittedBadge: "Admitted",
		party:
			"Party of {{total}} — {{alreadyIn}} already in, {{remaining}} still to come",
		admit: "Admit",
		reject: "Not this time",
		close: "Close",
	},
} as const;
